import type { WidgetConfig, SessionResponse, SendMessageResult } from '../types';

declare const ATTENDE_API_BASE: string;

/**
 * `data-api` costuma ser digitado só com o domínio (ex.: http://localhost:3001). Completa com
 * `/api/v1` quando faltar e remove barras finais, para as rotas `/widget/v1/...` baterem.
 */
export function normalizeApiBase(base: string): string {
  const trimmed = base.trim().replace(/\/+$/, '');
  return /\/api\/v\d+$/.test(trimmed) ? trimmed : `${trimmed}/api/v1`;
}

/** Erro HTTP da API do widget — `status` permite tratar 401 (token expirado) e 429 (limite). */
export class ApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export class ApiClient {
  private baseUrl: string;

  constructor(apiBase?: string) {
    this.baseUrl = normalizeApiBase(apiBase ?? ATTENDE_API_BASE);
  }

  async fetchConfig(tenantId: string): Promise<WidgetConfig> {
    const res = await fetch(`${this.baseUrl}/widget/v1/config?tenant=${encodeURIComponent(tenantId)}`, {
      mode: 'cors',
      headers: { 'Accept': 'application/json' },
    });
    if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
    return res.json() as Promise<WidgetConfig>;
  }

  async createSession(params: {
    tenant: string;
    visitorId: string;
    url: string;
    userAgent: string;
    language: string;
    referrer: string;
  }): Promise<SessionResponse> {
    const res = await fetch(`${this.baseUrl}/widget/v1/sessions`, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
    if (!res.ok) throw new ApiError(res.status, `Session creation failed: ${res.status}`);
    return res.json() as Promise<SessionResponse>;
  }

  async sendMessage(sessionToken: string, content: string, clientMessageId: string): Promise<SendMessageResult> {
    const res = await fetch(`${this.baseUrl}/widget/v1/messages`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: JSON.stringify({ type: 'text', content, clientMessageId }),
    });
    if (!res.ok) throw new ApiError(res.status, `Send message failed: ${res.status}`);
    return res.json() as Promise<SendMessageResult>;
  }

  async identify(sessionToken: string, params: { userId: string; email?: string; name?: string; hmac?: string }): Promise<void> {
    const res = await fetch(`${this.baseUrl}/widget/v1/identify`, {
      method: 'POST',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionToken}`,
      },
      body: JSON.stringify(params),
    });
    if (!res.ok) throw new ApiError(res.status, `Identify failed: ${res.status}`);
  }

  getStreamUrl(sessionToken: string): string {
    return `${this.baseUrl}/widget/v1/stream?token=${encodeURIComponent(sessionToken)}`;
  }
}
